
import React, { useMemo } from 'react';
import { SignedIn, SignedOut } from '@clerk/clerk-react';
import { Routes, Route, Navigate, useLocation, useRoutes } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { AppProvider, useAppContext } from './hooks/useAppContext';
import { useTranslation } from 'react-i18next';

import ImageUploadView from '../components/ImageUploadView';
import ConfigurationView from '../components/ConfigurationView';
import ResultsView from '../components/ResultsView';
import ProblemHistoryView from '../components/ProblemHistoryView';
import LoadingSpinner from '../components/LoadingSpinner';
import Header from '../components/Header';
import ErrorIcon from '../components/icons/ErrorIcon';
import Button from '../components/Button';
import ConfirmationModal from '../components/ConfirmationModal';
import DashboardView from '../components/DashboardView';
import AccountView from '../components/AccountView';
import BackButton from '../components/BackButton';
import TransactionHistoryView from '../components/TransactionHistoryView';
import LandingPage from '../components/LandingPage';
import PaddleCheckoutView from '../components/PaddleCheckoutView';
import BottomNavBar from '../components/BottomNavBar';
import ResultsViewSkeleton from '../components/ResultsViewSkeleton';
import BackToTopButton from '../components/BackToTopButton';
import HelperMenu from '../components/HelperMenu';

const ProtectedApp: React.FC = () => {
  const {
    isLoading,
    problemDetails,
    solutionData,
    error,
    dashboardData,
    accountData,
    handleImageUpload,
    solveProblemAction,
    navigateToHome,
    navigateToHistory,
    navigateToDashboard,
    navigateToAccount,
    navigateToAccountTransactions,
    navigateToCheckout,
    handleBackToConfig,
    requestConfirmation,
    openHelperMenu,
    closeHelperMenu,
    handleCheckoutCompleted,
    confirmationState, closeConfirmation, isHelperMenuOpen,
  } = useAppContext();

  const { t } = useTranslation();
  const location = useLocation();

  const cameFromSolveAttempt = location.state?.from === 'solve';

  const routes = useMemo(() => ([
    { path: "/", element: <ImageUploadView onImageUpload={handleImageUpload} /> },
    {
      path: "/configure", element: (
        problemDetails ? (
          <div className="relative pt-12">
            <ConfigurationView
              problemImage={problemDetails.imageDataUrl}
              initialConfig={{ grade: problemDetails.grade, language: problemDetails.language }}
              onSubmit={solveProblemAction}
            />
          </div>
        ) : (
          <Navigate to="/app" replace />
        )
      )
    },
    {
      path: "/solution", element: (
        isLoading ? (
          <ResultsViewSkeleton />
        ) : solutionData ? (
          <ResultsView solution={solutionData} onSolveAnother={navigateToHome} />
        ) : (
          <Navigate to="/app" replace />
        )
      )
    },
    {
      path: "/history", element: (
        <div className="relative pt-12">
          <ProblemHistoryView onGoToSolve={navigateToHome} requestConfirmation={requestConfirmation} />
        </div>
      )
    },
    {
      path: "/dashboard", element: (
        <div className="relative pt-12">
          <DashboardView
            dashboardData={dashboardData}
            onGoToHistory={navigateToHistory}
            onGoToAccount={navigateToAccount}
          />
        </div>
      )
    },
    {
      path: "/account", element: (
        <div className="relative pt-12">
          <AccountView
            accountData={accountData}
            onShowTransactionHistory={() => navigateToAccountTransactions()}
            onGoToCheckout={navigateToCheckout}
            onOpenHelperMenu={openHelperMenu}
          />
        </div>
      )
    },
    {
      path: "/account/transactions", element: (
        <div className="relative pt-12">
          <TransactionHistoryView />
        </div>
      )
    },
    {
      path: "/checkout", element: (
        <div className="relative pt-12">
          <BackButton
            onClick={() => cameFromSolveAttempt ? navigateToHome() : navigateToAccount()}
            className="absolute top-0 left-0"
            title={cameFromSolveAttempt ? t('app.back_to_config') : t('app.back_to_account')} />
          <PaddleCheckoutView onCheckoutCompleted={handleCheckoutCompleted} />
        </div>
      )
    },
    { path: "*", element: <Navigate to="/app/" replace /> }
  ]), [
    handleImageUpload, problemDetails, navigateToHome, solveProblemAction,
    isLoading, solutionData, requestConfirmation, dashboardData, navigateToHistory, navigateToAccount,
    accountData, navigateToCheckout, cameFromSolveAttempt, handleCheckoutCompleted, openHelperMenu, t
  ]);

  const element = useRoutes(routes);

  const renderContent = () => {
    // Generic loader for non-solving navigations (e.g., dashboard, history)
    // The skeleton for the solution view is handled within the routes definition.
    if (isLoading && location.pathname !== '/app/solution') {
      return (
        <div className="flex flex-col items-center justify-center min-h-[calc(100vh-200px)]">
          <LoadingSpinner />
          <p className="mt-4 text-lg text-slate-600">{t('app.loading')}</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center text-center p-6 min-h-[calc(100vh-200px)]">
          <ErrorIcon className="w-16 h-16 text-red-500 mb-4" />
          <h2 className="text-2xl font-semibold text-red-600 mb-2">{t('app.error_title')}</h2>
          <p className="text-slate-700 mb-6 max-w-md">{typeof error === 'string' ? error : t('app.unknown_error')}</p>
          <div className="space-x-4">
            {problemDetails && <Button onClick={handleBackToConfig} variant="secondary">{t('app.back_to_config_button')}</Button>}
            <Button onClick={navigateToHome}>{t('app.try_again_button')}</Button>
          </div>
        </div>
      );
    }

    // If not loading and no error, render the animated routes
    return (
      <AnimatePresence mode="wait">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 15 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
        >
          {element}
        </motion.div>
      </AnimatePresence>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-sky-100 to-indigo-100">
      <Toaster position="top-center" reverseOrder={false} />
      <Header
        onLogoClick={navigateToHome}
        onHistoryClick={navigateToHistory}
        onDashboardClick={navigateToDashboard}
        onSolveClick={navigateToHome}
        onAccountClick={navigateToAccount}
        onHelperClick={openHelperMenu}
      />
      <main className="w-full p-4 md:p-8 flex-grow pb-20 md:pb-8 relative print:p-0">
        {renderContent()}
      </main>
      <footer className="w-full text-center p-4 text-sm text-slate-500 print:hidden">
        &copy; {new Date().getFullYear()} klikero.com {t('app.all_rights_reserved')}.
      </footer>
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        onClose={closeConfirmation}
        onConfirm={confirmationState.onConfirm}
        title={confirmationState.title}
        message={confirmationState.message}
      />
      <BottomNavBar
        onSolveClick={navigateToHome}
        onDashboardClick={navigateToDashboard}
        onHistoryClick={navigateToHistory}
        onAccountClick={navigateToAccount}
      />
      <BackToTopButton />
      <HelperMenu isOpen={isHelperMenuOpen} onClose={closeHelperMenu} />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/app/*"
        element={
          <>
            <SignedIn>
              <AppProvider>
                <ProtectedApp />
              </AppProvider>
            </SignedIn>
            <SignedOut>
              <Navigate to="/" replace />
            </SignedOut>
          </>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default App;
